import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiUser, FiLock, FiLogIn, FiEye, FiEyeOff } from 'react-icons/fi';
import axios from 'axios';
import './LoginPage.css';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [loginStatus, setLoginStatus] = useState(''); // 添加登录状态提示
  const [showPassword, setShowPassword] = useState(false);
  const [inputTimeout, setInputTimeout] = useState(null); // 输入防抖定时器
  const navigate = useNavigate();
  const location = useLocation();

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (inputTimeout) {
        clearTimeout(inputTimeout);
      }
    };
  }, [inputTimeout]);

  // 处理来自路由状态的消息（如退出登录成功提示）
  useEffect(() => {
    if (location.state && location.state.message) {
      toast.success(location.state.message);
      // 清除状态消息，避免刷新页面时重复显示
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // 清除之前的防抖定时器
    if (inputTimeout) {
      clearTimeout(inputTimeout);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 设置新的防抖定时器，确保用户输入完成
    const newTimeout = setTimeout(() => {
      setInputTimeout(null);
    }, 300); // 300ms防抖延迟
    
    setInputTimeout(newTimeout);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 防止重复提交
    if (loading) {
      return;
    }
    
    // 检查用户是否还在输入中
    if (inputTimeout) {
      toast.warning('请等待输入完成后再提交');
      return;
    }
    
    // 验证用户名
    const trimmedUsername = formData.username.trim();
    if (!trimmedUsername) {
      toast.error('请输入用户名');
      return;
    }
    
    // 验证密码
    const trimmedPassword = formData.password.trim();
    if (!trimmedPassword) {
      toast.error('请输入密码');
      return;
    }
    
    // 确保输入长度合理
    if (trimmedUsername.length < 2) {
      toast.error('用户名长度至少2个字符');
      return;
    }
    
    if (trimmedPassword.length < 3) {
      toast.error('密码长度至少3个字符');
      return;
    }

    setLoading(true);
    setLoginStatus('正在连接服务器...');
    
    // 增加重试机制
    const maxRetries = 3;
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        if (retryCount > 0) {
          setLoginStatus(`网络不稳定，正在重试 (${retryCount}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, 2000)); // 等待2秒后重试
        } else {
          setLoginStatus('正在验证用户信息...');
        }
        
        const response = await axios.post('/api/login', {
          username: trimmedUsername.toLowerCase(),
          password: trimmedPassword
        }, {
          timeout: 60000 // 增加超时时间到60秒
        });

        if (response.data.success) {
          setLoginStatus('登录成功，正在跳转...');
          toast.success('登录成功！');
          
          // 保存用户信息到localStorage
          localStorage.setItem('userInfo', JSON.stringify({
            userName: response.data.userName,
            name: response.data.name || response.data.userName,
            loginTime: new Date().toISOString(),
            isAdmin: Boolean(response.data.isAdmin)
          }));
          
          // 延迟跳转，让用户看到成功提示
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { 
                userName: response.data.userName,
                name: response.data.name || response.data.userName,
                isAdmin: Boolean(response.data.isAdmin),
                redirectUrl: response.data.redirectUrl
              } 
            });
          }, 1000);
          
          return; // 成功则退出重试循环
        } else {
          // 检查是否是维护模式
          if (response.data.maintenance) {
            // 显示维护信息的toast，使用更长的显示时间
            toast.error(response.data.message || '系统正在维护中，请稍后再试', {
              autoClose: 8000, // 8秒后自动关闭
              style: {
                whiteSpace: 'pre-line', // 支持换行显示
                maxWidth: '400px'
              }
            });
          } else {
            // 普通登录失败
            toast.error(response.data.message || '登录失败');
          }
          break;
        }
      } catch (error) {
        console.error('登录错误:', error);
        retryCount++;
        
        if (error.response) {
          // 服务器返回错误响应，检查是否是维护模式
          if (error.response.data && error.response.data.maintenance) {
            toast.error(error.response.data.message || '系统正在维护中，请稍后再试', {
              autoClose: 8000,
              style: {
                whiteSpace: 'pre-line',
                maxWidth: '400px'
              }
            });
          } else {
            toast.error(error.response.data?.message || '登录失败，请检查用户名和密码');
          }
          break;
        } else if (error.request) {
          // 网络错误，可以重试
          if (retryCount >= maxRetries) {
            toast.error('网络连接不稳定，请检查网络后重试');
          }
        } else {
          // 其他错误
          if (retryCount >= maxRetries) {
            toast.error('登录过程中发生错误，请稍后重试');
          }
        }
      }
    }
    
    setLoading(false);
    setLoginStatus('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          {/* 头部 */}
          <div className="login-header">
            <div className="logo">
              <div className="logo-icon">
                <FiUser size={32} />
              </div>
            </div>
            <h1 className="login-title">施工快捷请点</h1>
            <p className="login-subtitle">请输入您的OA账号密码进行登录</p>
          </div>

          {/* 登录表单 */}
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                <FiUser className="label-icon" />
                用户名
              </label>
              <input
                type="text"
                id="username"
                name="username"
                className="form-input"
                placeholder="请输入用户名"
                value={formData.username}
                onChange={handleInputChange}
                disabled={loading}
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                <FiLock className="label-icon" />
                密码
              </label>
              <div className="password-input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className="form-input password-input"
                  placeholder="请输入密码"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  disabled={loading}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            {/* 登录状态提示 */}
            {loginStatus && (
              <div className="login-status">
                <div className="status-indicator">
                  <div className="loading-spinner"></div>
                  <span>{loginStatus}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner"></div>
                  登录中...
                </>
              ) : (
                <>
                  <FiLogIn className="button-icon" />
                  登录
                </>
              )}
            </button>
          </form>

          {/* 底部信息 */}
          <div className="login-footer">
            <p className="footer-text">
              系统将通过安全验证确认您的身份
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
